class Breadcrumbs extends HTMLElement {
  trigger: HTMLButtonElement | null = null;
  mobilePopover: HTMLElement | null = null;
  mainBreadcrumbs: HTMLElement | null = null;
  mobileBreadcrumbs: HTMLElement | null = null;
  focusableElement: string = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
  checkIcon: string =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-4x text-green-800 shrink-0"><path fill-rule="evenodd" d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z" clip-rule="evenodd" /></svg>';
  boundKeydown: (e: KeyboardEvent) => void;

  constructor() {
    super();
    this.boundKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-trigger]');
    this.mobilePopover = this.querySelector('[data-popover]');
    this.mainBreadcrumbs = this.querySelector('[data-breadcrumbs]');
    this.mobileBreadcrumbs = this.querySelector('.m-breadcrumbs');

    this.trigger?.addEventListener('click', this.toggle.bind(this));
    this.copyBreadcrumbsToMobile();
    this.applyStyles();

    this.mobilePopover?.addEventListener('focusout', this.handleFocusOut.bind(this));
  }

  copyBreadcrumbsToMobile() {
    const mainList = this.mainBreadcrumbs?.querySelector('ol');
    const mobileList = this.mobileBreadcrumbs?.querySelector('ol');

    if (!mainList || !mobileList) return;

    mobileList.innerHTML = mainList.innerHTML;
  }

  applyStyles() {
    const links = this.mobileBreadcrumbs?.querySelectorAll('a');
    links?.forEach((l) => l.classList.add('py-1x'));

    // remove all divider on mobile
    this.mobileBreadcrumbs?.querySelectorAll('[data-divider]').forEach((s) => s.remove());
    // show hidden items on mobile
    this.mobileBreadcrumbs?.querySelector('li.hidden')?.classList.remove('hidden');
    // focusable last element
    this.mobileBreadcrumbs?.querySelectorAll('[itemprop="itemListElement"]').forEach((e) => {
      const focusable = e.querySelector(this.focusableElement);
      // current page breadcrumb item is not focusable
      if (!focusable) {
        const ele = e.querySelector('[itemprop="name"]');
        if (!ele) return;
        const content = ele.innerHTML;
        ele.innerHTML = '';
        // set focus and styles
        ele.setAttribute('tabindex', '0');
        ele.classList.add('focus:opacity-60', 'box-border', 'flex', 'justify-between', 'gap-2.5x');
        // add check
        ele.insertAdjacentHTML('beforeend', `<span class="truncate block">${content}</span>`);
        ele.insertAdjacentHTML('beforeend', this.checkIcon);
      }
    });
  }

  toggle() {
    const open = Boolean(this?.getAttribute('data-open'));

    if (open) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this?.setAttribute('data-open', 'true');
    document.addEventListener('keydown', this.boundKeydown);

    const focusable = this.getFocusableElements();
    if (focusable.length > 0) {
      focusable[0].focus();
    }
  }

  close() {
    this?.removeAttribute('data-open');
    document.removeEventListener('keydown', this.boundKeydown);
    this.trigger?.focus();
  }

  getFocusableElements(): HTMLElement[] {
    if (!this.mobilePopover) return [];
    return Array.from(this.mobilePopover.querySelectorAll<HTMLElement>(this.focusableElement)).filter(
      (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
    );
  }

  handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }

    if (e.key === 'Tab') {
      const focusable = this.getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // shift + tab
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // tab
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }

  handleFocusOut(_event: FocusEvent) {
    // Delay so activeElement is updated after blur
    requestAnimationFrame(() => {
      if (this.mobilePopover && !this.mobilePopover.contains(document.activeElement)) {
        this.close();
      }
    });
  }
}

if (!customElements.get('app-breadcrumbs')) {
  customElements.define('app-breadcrumbs', Breadcrumbs);
}
